import { useEffect, useState, type FormEvent } from 'react';
import type { JenisKomponenHonor } from '@app/shared';
import { AdminLayout } from '../../components/admin-layout';
import { Alert, Button, Card, Input, Modal, Table, Td, Th } from '../../components/ui';
import {
  getJenisKomponenHonorList,
  createJenisKomponenHonor,
  updateJenisKomponenHonor,
  deleteJenisKomponenHonor,
} from '../../features/jenis-komponen-honor/api';

const emptyForm = { kode: '', nama: '', deskripsi: '', urutan: 0, status_aktif: true };

export function JenisKomponenHonorPage() {
  const [items, setItems] = useState<JenisKomponenHonor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<JenisKomponenHonor | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await getJenisKomponenHonorList({ limit: 100 });
      setItems(result.rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat jenis komponen honor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: JenisKomponenHonor) => {
    setEditing(item);
    setForm({
      kode: item.kode,
      nama: item.nama,
      deskripsi: item.deskripsi ?? '',
      urutan: item.urutan,
      status_aktif: item.status_aktif,
    });
    setModalOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (editing) {
        await updateJenisKomponenHonor(editing.id, {
          nama: form.nama.trim(),
          deskripsi: form.deskripsi.trim() || null,
          urutan: form.urutan,
          status_aktif: form.status_aktif,
        });
        setMessage('Komponen diperbarui.');
      } else {
        await createJenisKomponenHonor({
          kode: form.kode.trim(),
          nama: form.nama.trim(),
          deskripsi: form.deskripsi.trim() || null,
          urutan: form.urutan,
        });
        setMessage('Komponen ditambahkan.');
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan komponen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm('Hapus komponen ini?')) return;
    try {
      await deleteJenisKomponenHonor(id);
      setMessage('Komponen dihapus.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus komponen');
    }
  };

  return (
    <AdminLayout title="Jenis Komponen Honor">
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p style={{ margin: 0, color: '#64748B' }}>
            Daftar jenis komponen penyusun honorarium (Honor JP, Kehadiran, Jabatan, dll).
          </p>
          <Button onClick={openAdd}>Tambah Komponen</Button>
        </div>
        {loading ? (
          <p>Memuat…</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#64748B' }}>Belum ada komponen. Tambahkan item pertama.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Kode</Th>
                <Th>Nama</Th>
                <Th>Deskripsi</Th>
                <Th>Urutan</Th>
                <Th>Status</Th>
                <Th style={{ width: 180 }}>Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <code>{item.kode}</code>
                  </Td>
                  <Td>{item.nama}</Td>
                  <Td>{item.deskripsi ?? '-'}</Td>
                  <Td>{item.urutan}</Td>
                  <Td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: item.status_aktif ? '#dcfce7' : '#fee2e2',
                        color: item.status_aktif ? '#166534' : '#991b1b',
                      }}
                    >
                      {item.status_aktif ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button variant="secondary" onClick={() => openEdit(item)}>
                        Ubah
                      </Button>
                      <Button variant="danger" onClick={() => void remove(item.id)}>
                        Hapus
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Ubah Komponen' : 'Tambah Komponen'}
      >
        <form onSubmit={submit}>
          {!editing && (
            <Input
              label="Kode"
              name="kode"
              value={form.kode}
              onChange={(e) => setForm({ ...form, kode: e.target.value })}
              required
              minLength={2}
              maxLength={30}
              placeholder="Misal: honor_jp, honor_kehadiran"
            />
          )}
          <Input
            label="Nama"
            name="nama"
            value={form.nama}
            onChange={(e) => setForm({ ...form, nama: e.target.value })}
            required
            minLength={3}
            maxLength={100}
          />
          <Input
            label="Deskripsi"
            name="deskripsi"
            value={form.deskripsi}
            onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
            maxLength={500}
          />
          <Input
            label="Urutan"
            name="urutan"
            type="number"
            min={0}
            value={String(form.urutan)}
            onChange={(e) => setForm({ ...form, urutan: Number(e.target.value) })}
          />
          <div style={{ marginBottom: '1rem' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={form.status_aktif}
                onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })}
              />
              Status Aktif
            </label>
          </div>
          <Button type="submit" disabled={saving}>
            {saving ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </form>
      </Modal>
    </AdminLayout>
  );
}
